/**
 * Determines whether a venue package can be booked for one completed booking
 * form. The selected section and services choice must match exactly, while
 * `all` remains a supported wildcard only for date-derived day/season fields.
 * Maximum capacities decide whether the package can physically accommodate a
 * request. Minimum capacities remain pricing-tier metadata and are used only
 * to rank the most appropriate package first. When a caller supplies both
 * section counts, a `both` package checks each section maximum independently.
 */

import type { Package } from '@domain/entities/Package';
import type {
  DayTypeValue,
  SeasonValue,
  SectionModeValue,
} from '@domain/value-objects/PackageEnums';

export interface BookingPackageCriteria {
  readonly sectionMode: SectionModeValue;
  readonly dayType: DayTypeValue;
  readonly season: SeasonValue;
  readonly hasIncludedServices: boolean;
  readonly guestCount: number;
  readonly guestCountMen?: number;
  readonly guestCountWomen?: number;
}

export function packageMatchesBookingCriteria(
  pkg: Package,
  criteria: BookingPackageCriteria
): boolean {
  const props = pkg.props;

  return (
    props.price.toSAR() > 0 &&
    props.sectionMode === criteria.sectionMode &&
    props.hasIncludedServices === criteria.hasIncludedServices &&
    (props.dayType === criteria.dayType || props.dayType === 'all') &&
    (props.season === criteria.season || props.season === 'all') &&
    validCount(criteria.guestCount) &&
    criteria.guestCount <= props.capacityMax &&
    splitCountsMatch(pkg, criteria)
  );
}

/** Whether the request falls inside this package's preferred pricing tier. */
export function packageContainsBookingGuestCounts(
  pkg: Package,
  criteria: Pick<
    BookingPackageCriteria,
    'guestCount' | 'guestCountMen' | 'guestCountWomen'
  >
): boolean {
  const props = pkg.props;
  if (
    !validCount(criteria.guestCount) ||
    criteria.guestCount < props.capacityMin ||
    criteria.guestCount > props.capacityMax
  ) {
    return false;
  }

  if (props.sectionMode !== 'both') return true;
  if (
    !validCount(criteria.guestCountMen) ||
    !validCount(criteria.guestCountWomen) ||
    criteria.guestCountMen + criteria.guestCountWomen !== criteria.guestCount
  ) {
    return false;
  }

  return (
    props.capacityMenMin! <= criteria.guestCountMen &&
    criteria.guestCountMen <= props.capacityMenMax! &&
    props.capacityWomenMin! <= criteria.guestCountWomen &&
    criteria.guestCountWomen <= props.capacityWomenMax!
  );
}

function splitCountsMatch(
  pkg: Package,
  criteria: BookingPackageCriteria
): boolean {
  if (
    criteria.guestCountMen === undefined &&
    criteria.guestCountWomen === undefined
  ) {
    return true;
  }
  if (
    pkg.props.sectionMode !== 'both' ||
    !validCount(criteria.guestCountMen) ||
    !validCount(criteria.guestCountWomen)
  ) {
    return false;
  }
  return (
    criteria.guestCountMen + criteria.guestCountWomen ===
      criteria.guestCount &&
    criteria.guestCountMen <= pkg.props.capacityMenMax! &&
    criteria.guestCountWomen <= pkg.props.capacityWomenMax!
  );
}

function validCount(value: number | undefined): value is number {
  return value !== undefined && Number.isInteger(value) && value > 0;
}

export function bookingPackageSpecificity(pkg: Package): number {
  return (
    (pkg.props.dayType !== 'all' ? 1 : 0) +
    (pkg.props.season !== 'all' ? 1 : 0)
  );
}
